import { defineStore } from 'pinia';
import api from '@/api/api';
import type { ZRecommendation } from '@schemas/recommendation.schemas';
import type {Id} from "@typesDef/general.types";
import {setIdsUnique} from "@/stores/helpers/storeHelper";

export const useRecommendationStore = defineStore('recommendation', {
    state: () => ({
        // NOTE one elements is based on aggregation and is not a ZRecommendation (see recommendation.model.ts)
        recommendationsById: {} as Record<Id, ZRecommendation>,
        // all recommendations that are related to the current user, either as author or as receiver,
        // we can use this to show them in the profile and also to show them in the event details page
        myRecommendationIds: [] as Id[]
        // TODO it might interesting to have public recommendations as well,
        //  so that we which announcers recommendate for which events, but we can also get this from the event details page, so we will see if we need it in the future
    }),
    getters: {
        myRecommendations(state): ZRecommendation[] {
            return state.myRecommendationIds.map(id => state.recommendationsById[id]).filter(Boolean) as ZRecommendation[];
        }
    },
    actions: {
        clearMyData(): void {
            this.recommendationsById = {};
            this.myRecommendationIds = [];
        },
        // FIXME move them to general library since they are used in multiple stores
        upsertMany(docs: ZRecommendation[]) {
            for (const e of docs) this.recommendationsById[e._id] = e
        },
        /**
         * Fetch recommendations for a specific event
         * @param eventId
         */
        async fetchRecommendationsForEvent(eventId: string): Promise<ZRecommendation[]> {
            const docs = await api.getDocuments<ZRecommendation>('recommendations/event/' + eventId);
            this.upsertMany(docs);
            setIdsUnique(this.myRecommendationIds, docs.map(d => d._id));
            return docs;
        },

        /**
         * Fetch recommendations for a specific announcer
         * @param announcerId
         */
        async fetchRecommendationsForAnnouncer(announcerId: string): Promise<ZRecommendation[]> {
            const docs = await api.getDocuments<ZRecommendation>('recommendations/announcer/' + announcerId);
            this.upsertMany(docs);
            setIdsUnique(this.myRecommendationIds, docs.map(d => d._id));
            return docs;
        },
        /**
         * Add a new recommendation
         * There is no update only post.
         * @param recommendation
         */
        async addRecommendation(recommendation: Partial<ZRecommendation>): Promise<ZRecommendation> {
            const created = await api.postDocument<ZRecommendation>('recommendations', recommendation);
            if(!created || !created._id) {
                throw new Error('Failed to create recommendation');
            }
            const newId = created._id;
            this.recommendationsById[newId] = created;
            // add to myRecommendationIds if not already there
            if (!this.myRecommendationIds.includes(newId)) {
                this.myRecommendationIds.unshift(newId);
            }
            return created;
        },

        /**
         * Remove a recommendation
         * @param recommendationId
         */
        async removeRecommendation(recommendationId: Id): Promise<void> {
            await api.deleteDocument<ZRecommendation>('myRecommendations', recommendationId);
            delete this.recommendationsById[recommendationId];
            this.myRecommendationIds = this.myRecommendationIds.filter(id => id !== recommendationId);
        }
    }
});
